// The site types rumio.world markets, and the per-type version of the site
// each one gets (/{slug}, /{slug}/features, /{slug}/pricing). One entry here
// drives the selector on the home page, the routes in App.tsx, the type
// switcher, which pricing-matrix rows and partner products are shown, and the
// `?type=` handed to signup. Copy lives under `verticals.{id}` in
// functions/locales/en/saas.json.
//
// `id` is the platform SiteType the Site Builder provisions, so it can be
// passed straight through. The slug list is mirrored for the sitemap in
// functions/src/routes/seo.ts (PLATFORM_VERTICAL_SLUGS) — a test asserts the
// two agree.
import { BedDouble, Landmark, Rocket, ShoppingBag, type LucideIcon } from 'lucide-react';

export type VerticalId = 'ecommerce' | 'lodging' | 'museum' | 'saas';
export type VerticalPage = '' | 'features' | 'pricing';

export interface Vertical {
  id: VerticalId;
  slug: string;
  icon: LucideIcon;
  /** Partner products (by product family) that make sense for this type. */
  addonFamilies: string[];
}

export const VERTICALS: Vertical[] = [
  { id: 'ecommerce', slug: 'stores', icon: ShoppingBag, addonFamilies: ['axiss-support', 'etaxengine'] },
  { id: 'lodging', slug: 'lodging', icon: BedDouble, addonFamilies: ['axiss-support'] },
  { id: 'museum', slug: 'nonprofits', icon: Landmark, addonFamilies: ['axiss-support'] },
  { id: 'saas', slug: 'saas', icon: Rocket, addonFamilies: ['axiss-support'] },
];

const BY_ID = new Map(VERTICALS.map((v) => [v.id, v]));
const BY_SLUG = new Map(VERTICALS.map((v) => [v.slug, v]));

export function isVerticalId(value: unknown): value is VerticalId {
  return typeof value === 'string' && BY_ID.has(value as VerticalId);
}

export function getVertical(id: VerticalId): Vertical {
  return BY_ID.get(id)!;
}

/** `/lodging/pricing` → lodging. Null for the type-neutral pages. */
export function verticalFromPath(pathname: string): Vertical | null {
  const first = pathname.split('/')[1] ?? '';
  return BY_SLUG.get(first) ?? null;
}

/** The type's own URL for a page; the type-neutral URL when there is no type. */
export function verticalPath(id: VerticalId | null | undefined, page: VerticalPage = ''): string {
  const tail = page ? `/${page}` : '';
  if (!id) return tail || '/';
  return `/${getVertical(id).slug}${tail}`;
}

/** Signup link carrying the type (and optionally the tier) into onboarding. */
export function signupPath(id: VerticalId | null | undefined, plan?: string): string {
  const params = new URLSearchParams();
  if (id) params.set('type', id);
  if (plan) params.set('plan', plan);
  const qs = params.toString();
  return qs ? `/signup?${qs}` : '/signup';
}

const STORAGE_KEY = 'rumio_vertical';

export function rememberVertical(id: VerticalId): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* private mode / disabled storage — silent no-op */
  }
}

export function recallVertical(): VerticalId | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isVerticalId(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Rewrites a type-neutral marketing link (`/features`, `/pricing`) to the
 * given type's version. Anything else is returned untouched.
 */
export function verticalHref(href: string, id: VerticalId | null | undefined): string {
  if (!id) return href;
  if (href === '/features') return verticalPath(id, 'features');
  if (href === '/pricing') return verticalPath(id, 'pricing');
  return href;
}
